import { parse, formatISO, isValid, isAfter, isSameDay, addDays, startOfToday } from 'date-fns';
import { notification } from 'antd';
import { t } from '../i18n/i18n';

/**
 * Formats the `limitDate` property of each invitation object in the given array.
 * Parses the `limitDate` string using the format 'yy-MM-dd HH:mm' and converts it to an ISO string.
 *
 * @param {Array<Object>} csvInvitations - Array of invitation objects imported from a CSV file.
 * @returns {Array<Object>} A new array of invitation objects with the formatted `limitDate` property.
 */
export const formatLimitDateToISO = (csvInvitations) => {
  const invalidDates = [];
  const pastDates = [];
  const today = startOfToday();

  const formatted = csvInvitations?.map((x, i) => {
    let limitDate;
    // Check if limitDate is empty string, null, or undefined
    if (!x.limitDate || x.limitDate === '') {
      // Set default limit date to current date + 7 days
      const defaultDate = addDays(new Date(), 7);
      limitDate = formatISO(defaultDate);
    } else {
      try {
        // Try multiple date formats for better compatibility
        const formats = [
          'yy-MM-dd HH:mm', // Primary format (2-digit year)
          'yyyy-MM-dd HH:mm', // Alternative with 4-digit year
          'dd/MM/yy HH:mm', // Alternative format
          'MM/dd/yy HH:mm', // US format with 2-digit year
        ];

        let parsed;
        let validFormat = false;

        for (const format of formats) {
          try {
            parsed = parse(x.limitDate, format, new Date());
            if (isValid(parsed)) {
              validFormat = true;
              break;
            }
          } catch {
            // Try next format
          }
        }

        if (validFormat && isValid(parsed)) {
          // Check if date is in the past (before today)
          if (!isAfter(parsed, today) && !isSameDay(parsed, today)) {
            pastDates.push({
              row: i + 1,
              value: x.limitDate,
              email: x.email,
              parsedDate: parsed,
            });
            // Don't set the limitDate if it's in the past
            limitDate = undefined;
          } else {
            const isoString = formatISO(parsed);
            limitDate = isoString;
          }
        } else {
          invalidDates.push({
            row: i + 1,
            value: x.limitDate,
            email: x.email,
          });
        }
      } catch (error) {
        console.error(`Error parsing row ${i + 1} invitation limitDate:`, error);
        invalidDates.push({
          row: i + 1,
          value: x.limitDate,
          email: x.email,
        });
      }
    }
    return {
      ...x,
      limitDate,
    };
  });

  // Show notification if there were invalid dates
  if (invalidDates.length > 0) {
    const errorRows = invalidDates
      .slice(0, 5)
      .map((err) => `${t('ROW')} ${err.row}: ${err.email} - "${err.value}"`)
      .join('\n');

    notification.error({
      message: t('SCREENING_IMPORT_DATE_FORMAT_ERROR'),
      description: (
        <div>
          <p>{t('INVALID_DATE_FORMAT_IN_CSV')}</p>
          <pre style={{ fontSize: '11px', marginTop: '8px' }}>{errorRows}</pre>
          {invalidDates.length > 5 && (
            <p>{t('AND_MORE').replace('{count}', invalidDates.length - 5)}</p>
          )}
          <p style={{ marginTop: '8px' }}>
            <b>{t('EXPECTED_FORMAT')}:</b> YY-MM-DD HH:mm
          </p>
        </div>
      ),
      duration: 8,
      placement: 'topRight',
    });
  }

  // Show warning if there were past dates
  if (pastDates.length > 0) {
    const pastDateRows = pastDates
      .slice(0, 5)
      .map((err) => `${t('ROW')} ${err.row}: ${err.email} - "${err.value}"`)
      .join('\n');

    notification.warning({
      message: t('SCREENING_IMPORT_PAST_DATES_WARNING'),
      description: (
        <div>
          <p>{t('PAST_DATES_FOUND_IN_CSV')}</p>
          <pre style={{ fontSize: '11px', marginTop: '8px' }}>{pastDateRows}</pre>
          {pastDates.length > 5 && <p>{t('AND_MORE').replace('{count}', pastDates.length - 5)}</p>}
        </div>
      ),
      duration: 10,
      placement: 'topRight',
    });
  }

  return formatted;
};
